import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import invariant from "tiny-invariant";

import { SilencedError } from "../../app/utilities/silenced-error";
import { useAccessControl } from "../../app/use-access-control";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useCenterConstants, type SubServiceDictionary } from "../use-center";
import { useFamilyConstants } from "../use-families";
import { inquiryQueryKeys } from "./query-keys";
import type { Inquiry, InquiryFormData, InquiryPayload, InquiryResponse, InquiryStage } from "./types";
import { referenceRecordToDiscountTypeSymbol, toDiscountTypeCode } from "./discount-type";
import { serializeInquiryForApi } from "./inquiry-request-payload";
import moment from "moment";

const transformResponseToFormData = (
  data: InquiryResponse,
  subServicesDict: SubServiceDictionary,
  timezone: string
) => {
  const subjects = subServicesDict?.[data.program.value]?.subjects || [];
  const tests = subServicesDict?.[data.program.value]?.tests || [];
  const totalLength = subjects.length + tests.length;
  const subjectOrTest = data.subjects?.length ? "subjectId" : "testId";
  const subjectOrTestId = data.subjects?.[0]?.value || data.test?.value;
  const selectedSubjectOrTest = subjectOrTestId ? `${subjectOrTestId}::${subjectOrTest}` : "";
  const inquiryTimestamp = moment.utc(`${data.inquiryDate}T${data.inquiryTimeReceived}`).tz(timezone);
  const discountType = referenceRecordToDiscountTypeSymbol(data.aeFee?.discountType);
  const discountTypeId = toDiscountTypeCode(discountType) ?? "";

  const formData = {
    id: data.id,
    legacyClientId: data.legacyClientId,
    family: data.family,
    familyId: data.family.id,
    inquirer: data.inquirer,
    inquirerId: data.inquirer.id,
    student: data.student,
    studentId: data.student.id,
    relationshipId: data.relationshipId,
    selectedSubService: `${data.program.value}::${totalLength}`,
    selectedSubjectOrTest,
    serviceId: data.service?.value,
    programId: data.program?.value,
    subjectId: data.subjects?.[0]?.value,
    multipleSubjectIds: data.subjects?.map(subject => subject.value) || [],
    testId: data.test?.value,
    inquiryDate: inquiryTimestamp.format("YYYY-MM-DD"),
    inquiryTimeReceived: inquiryTimestamp.format("HH:mm"),
    sourceId: data.source?.value,
    referredById: data.referredBy?.value,
    referredByOther: data.referredByOther,
    regFee: (parseFloat(data.regFee?.toString()) || 0).toFixed(2),
    aeFee: {
      discountType,
      discountTypeId,
      discountValue: (parseFloat(data.aeFee?.discountValue?.toString()) || 0).toFixed(2),
      baseFee: (parseFloat(data.aeFee.baseFee?.toString()) || 0).toFixed(2),
      discountReasonId: data.aeFee?.discountReason?.value ?? "",
      discountReasonOther: data.aeFee?.discountReasonOther ?? ""
    },
    standardHourlyTuitionQuoted: (parseFloat(data.standardHourlyTuitionQuoted?.toString()) || 0).toFixed(2),
    privateHourlyTuitionQuoted: (parseFloat(data.privateHourlyTuitionQuoted?.toString()) || 0).toFixed(2),
    notes: data.notes,
    takenBy: {
      id: data.takenBy.externalId,
      givenName: data.takenBy.firstName,
      surname: data.takenBy.lastName,
      isDynamicsUser: data.takenBy.isDynamicsUser
    },
    takenById: data.takenBy.externalId,
    cecManaged: data.cecManaged
  } as InquiryFormData;

  return formData;
};

export const useInquiry = (inquiryId?: string) => {
  const { activeCenterId } = useAccessControl();
  const { getToken } = useAccessToken();
  const { activeConstants } = useCenterConstants(activeCenterId);
  const {
    chainableFieldsDict: { subServicesDict }
  } = activeConstants;

  const hasSubServices = !!Object.keys(subServicesDict).length;

  return useQuery({
    queryKey: inquiryQueryKeys.detail(activeCenterId, inquiryId),
    enabled: Boolean(inquiryId) && hasSubServices,
    queryFn: async () => {
      invariant(inquiryId, "inquiryId is required");
      return getInquiry(await getToken(), activeCenterId, inquiryId);
    },
    select: data => transformResponseToFormData(data, subServicesDict, activeConstants.timezone)
  });
};

export const useInquirySummary = (inquiryId?: string) => {
  const { activeCenterId } = useAccessControl();
  const { getToken } = useAccessToken();

  return useQuery({
    queryKey: inquiryQueryKeys.detail(activeCenterId, inquiryId),
    enabled: Boolean(inquiryId),
    queryFn: async () => {
      invariant(inquiryId, "inquiryId is required");
      return getInquiry(await getToken(), activeCenterId, inquiryId);
    },
    select: data => {
      const summary: Inquiry = {
        id: data.id,
        familyId: +data.familyId,
        studentId: data.student.id,
        studentName: data.student.displayName,
        stage: data.inquiryStage as InquiryStage,
        status: data.inquiryStatus,
        statusStartDate: "",
        stageStartDate: "",
        service: data.service.value.toString(),
        isProcessingStatus: data.isProcessingStatus,
        centerId: data.centerId
      };
      return summary;
    }
  });
};

export const useCreateOrUpdateInquiry = () => {
  const { activeCenterId } = useAccessControl();
  const { getToken } = useAccessToken();
  const queryClient = useQueryClient();
  const { OTHER_VALUE } = useFamilyConstants();
  const isSubmittingRef = useRef(false);

  return useMutation({
    mutationFn: async ({ inquiryId, formData }: { inquiryId?: string; formData: InquiryFormData }) => {
      if (isSubmittingRef.current) {
        throw new SilencedError("Inquiry save already in progress");
      }
      isSubmittingRef.current = true;

      try {
        const discountTypeId = toDiscountTypeCode(formData.aeFee.discountType);
        const formDataWithDefaults = {
          ...formData,
          relationshipId: formData.relationshipId || `${OTHER_VALUE}`,
          aeFee: {
            ...formData.aeFee,
            discountTypeId
          }
        };

        return await (inquiryId
          ? updateInquiry(await getToken(), activeCenterId, inquiryId, formDataWithDefaults)
          : createInquiry(await getToken(), activeCenterId, formDataWithDefaults));
      } finally {
        isSubmittingRef.current = false;
      }
    },
    onSuccess: payload => {
      if (!payload?.id) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: inquiryQueryKeys.list({ centerId: activeCenterId }) });
      queryClient.invalidateQueries({ queryKey: inquiryQueryKeys.baseDashboard() });
      queryClient.invalidateQueries({
        queryKey: inquiryQueryKeys.detail(activeCenterId, payload.id)
      });
    }
  });
};

export const getInquiry = (accessToken: string, centerId: string, inquiryId: string) => {
  return callLcosService<InquiryResponse>(accessToken, `/api/inquiries/${centerId}/${inquiryId}`);
};

export const createInquiry = (accessToken: string, centerId: string, payload: InquiryFormData) => {
  return callLcosService<InquiryPayload>(
    accessToken,
    `/api/inquiries/${centerId}`,
    "POST",
    serializeInquiryForApi(payload, { includeRelationshipId: true, includeClientRequestId: true })
  );
};

export const updateInquiry = (accessToken: string, centerId: string, inquiryId: string, payload: InquiryFormData) => {
  return callLcosService<InquiryPayload>(
    accessToken,
    `/api/inquiries/${centerId}/${inquiryId}`,
    "PUT",
    serializeInquiryForApi(payload, { includeRelationshipId: true })
  );
};
